"use client";

import { useState, useTransition } from "react";
import type { ProductPage } from "@/lib/shopify/types";
import type { SortSlug } from "@/lib/sorting";
import { ProductGrid } from "./ProductCard";
import { loadMoreProducts, type ListingSource } from "./listing-actions";

type Props = { initial: ProductPage; source: ListingSource; sort: SortSlug };

// Give this a `key` that changes with sort and filters so it starts over.
export function ProductListing({ initial, source, sort }: Props) {
  const [products, setProducts] = useState(initial.products);
  const [pageInfo, setPageInfo] = useState(initial.pageInfo);
  const [failed, setFailed] = useState(false);
  const [loading, startTransition] = useTransition();

  const showMore = () =>
    startTransition(async () => {
      setFailed(false);
      try {
        const next = await loadMoreProducts(source, sort, pageInfo.endCursor);
        setProducts((current) => [...current, ...next.products]);
        setPageInfo(next.pageInfo);
      } catch {
        setFailed(true);
      }
    });

  return (
    <>
      <ProductGrid products={products} preloadFirst />
      {pageInfo.hasNextPage ? (
        <div className="mt-14 flex flex-col items-center gap-3">
          <button type="button" className="btn btn-secondary" onClick={showMore} disabled={loading}>
            {loading ? "Loading more" : "Show more"}
          </button>
          {failed ? (
            <p role="alert" className="text-[0.9375rem] text-gold">
              The next products didn&apos;t load. Check your connection and try again.
            </p>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
